import axios from 'axios';
import { toast } from 'react-toastify';
import { clearCache } from './apiCache';
import config from '../config';
import { getIdleSeconds } from './activityTracker';

const axiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
});

// ── علامة تسجيل الخروج — لمنع الطلبات المعلقة من إنتاج أخطاء في Console
let _isLoggingOut = false;
export function setLoggingOut(value: boolean): void { _isLoggingOut = value; }

// إضافة Interceptor لإرفاق Token و FiscalYearId مع كل request
axiosInstance.interceptors.request.use(
  (axiosConfig) => {
    // إذا المستخدم يسجل خروج — إلغاء كل الطلبات الجديدة بصمت
    if (_isLoggingOut) {
      const controller = new AbortController();
      axiosConfig.signal = controller.signal;
      controller.abort();
      return axiosConfig;
    }

    const token = localStorage.getItem('token');
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`;

      // قراءة FiscalYearId من Token نفسه
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.FiscalYearId) {
          axiosConfig.headers['X-Fiscal-Year-Id'] = payload.FiscalYearId;
        }
      } catch {
        // token غير صالح — لا حاجة لطباعة خطأ
      }
    }

    // إذا المستخدم بدل السنة يدوياً من Dropdown
    const selectedFiscalYearId = localStorage.getItem('selectedFiscalYearId');
    if (selectedFiscalYearId) {
      axiosConfig.headers['X-Fiscal-Year-Id'] = selectedFiscalYearId;
    }

    return axiosConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor للتعامل مع الأخطاء - حماية شاملة من الصفحة الحمراء
axiosInstance.interceptors.response.use(
  (response) => {
    // حماية: إذا كان GET request والـ response.data ليس array ولكن المتوقع array
    // نتركه كما هو - الحماية الأساسية في 403
    return response;
  },
  (error) => {
    // طلبات ملغية (أثناء تسجيل الخروج) — تجاهل بصمت تام
    if (axios.isCancel(error) || _isLoggingOut) {
      return Promise.reject(error);
    }

    // 401 - انتهاء الجلسة
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        _isLoggingOut = true; // منع طلبات إضافية
        clearCache();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('fiscalYears');
        localStorage.removeItem('currentFiscalYearId');
        localStorage.removeItem('selectedFiscalYearId');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    // 503 - وضع الصيانة — إبلاغ التطبيق فوراً
    if (error.response?.status === 503 && error.response?.data?.maintenanceMode) {
      window.dispatchEvent(new Event('maintenance-mode-activated'));
      return Promise.reject(error);
    }

    // 403 - ليس لديه صلاحية
    if (error.response?.status === 403) {
      const language = localStorage.getItem('language') || 'en';
      const method = error.config?.method?.toUpperCase() || 'GET';
      
      // GET requests: إرجاع مصفوفة فارغة بدون toast (بيانات مساعدة مثل Concepts, PaymentMethods, Branches)
      if (method === 'GET') {
        return Promise.resolve({ 
          data: [],
          status: 403,
          statusText: 'Forbidden',
          headers: {},
          config: error.config
        });
      }
      
      // POST/PUT/DELETE: إظهار رسالة خطأ
      const message = language === 'ar' 
        ? 'ليس لديك صلاحية لتنفيذ هذه العملية' 
        : 'You do not have permission to perform this action';
      
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'permission-denied',
      });

      // AUDIT FIX: a denied write must REJECT, not resolve — otherwise the caller's try/catch
      // never fires and the UI falsely reports "saved" while nothing was created/updated.
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;

// ── Heartbeat ────────────────────────────────────────────────────────────────
// Pings /auth/activity every 2 minutes when user is authenticated AND has had
// real input activity in the last 2 minutes. This keeps IsOnline/LastActivityDate
// honest on the backend so Session Monitor's "Sleep" status reflects reality.
let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
const ACTIVITY_FRESHNESS_SECONDS = 120;

export function startHeartbeat(): void {
  if (_heartbeatTimer) return;
  _heartbeatTimer = setInterval(() => {
    if (!localStorage.getItem('token')) return;
    // Skip if user has been idle longer than the freshness window
    if (getIdleSeconds() > ACTIVITY_FRESHNESS_SECONDS) return;
    axiosInstance.post('/Auth/activity').catch(() => {});
  }, HEARTBEAT_INTERVAL_MS);
}

export function stopHeartbeat(): void {
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
}